import {Banner} from '@primer/react'
import type {Ref} from 'react'

import type {DisabledRepoStaffBannerPayload, DisabledRepoStaffBannerReason} from './types'

type DisabledRepoStaffBannerProps = DisabledRepoStaffBannerPayload & {
  ref?: Ref<HTMLElement>
}

const reasonLabels = {
  admin: 'administrator action',
  dmca: 'DMCA takedown',
  ['private_information']: 'private information violation',
  ['sensitive_data']: 'sensitive data violation',
  size: 'abusive size',
  tos: 'Terms of Service violation',
  trademark: 'trademark violation',
} satisfies Record<DisabledRepoStaffBannerReason, string>

function reasonLabel(reason?: DisabledRepoStaffBannerReason | null): string | undefined {
  return typeof reason === 'string' && Object.hasOwn(reasonLabels, reason) ? reasonLabels[reason] : undefined
}

const staffAccessWarning =
  'You are viewing it because you have authorized staff access. Do not share the contents of this repository externally or with staff who do not have this entitlement.'

export function DisabledRepoStaffBanner({repositoryNwo, reason, ref}: DisabledRepoStaffBannerProps) {
  if (typeof repositoryNwo !== 'string') return <DisabledRepoStaffBannerFallback ref={ref} />

  const label = reasonLabel(reason)

  return (
    <Banner
      ref={ref}
      variant="critical"
      title="Staff view"
      hideTitle
      flush
      description={
        <>
          <strong>Staff view:</strong>{' '}
          <span>
            {label ? (
              <>
                This repository ({repositoryNwo}) has been disabled due to <strong>{label}</strong>.{' '}
                {staffAccessWarning}
              </>
            ) : (
              <>
                This repository ({repositoryNwo}) has been disabled for an unknown reason. {staffAccessWarning}
              </>
            )}
          </span>
        </>
      }
    />
  )
}

export function DisabledRepoStaffBannerFallback({ref}: {ref?: Ref<HTMLElement>}) {
  return (
    <Banner
      ref={ref}
      variant="critical"
      title="Staff view"
      hideTitle
      flush
      description={
        <>
          <strong>Staff view:</strong> This repository has been disabled. {staffAccessWarning}
        </>
      }
    />
  )
}
